/** Current history bounds use the same saved Year and timezone as rule evaluation. */
import { collection, doc, getDocFromServer, getDocsFromServer, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { SchoolRulePeriodError, isSchoolRulePeriodError, resolveSchoolHistoryYear, schoolRuleTimeZone } from './domain/schoolRulePeriods';
import { watchSchoolHistoryYear } from './domain/schoolHistoryYearSubscription';

export function subscribeAcademicYearBounds(schoolId, receive, onError, onPending) {
  return watchSchoolHistoryYear({ receive, onError, onPending,
    listen: (kind, next, error) => onSnapshot(kind === 'school'
      ? doc(db, 'schools', schoolId) : collection(db, 'schools', schoolId, 'schoolDatePeriods'),
    { includeMetadataChanges: true }, snapshot => next(kind === 'school'
      ? snapshot.exists() ? snapshot.data() : null
      : snapshot.docs.map(row => ({ ...row.data(), id: row.id })), snapshot.metadata), error),
    onResume: refresh => {
      const visible = () => { if (document.visibilityState === 'visible') refresh(); };
      window.addEventListener('focus', refresh); document.addEventListener('visibilitychange', visible);
      return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', visible); };
    },
  });
}

export async function getAcademicYearBounds(schoolId) {
  if (!schoolId) throw new SchoolRulePeriodError('A school is required to read its Time Periods.');
  try {
    const [periods, school] = await Promise.all([
      getDocsFromServer(collection(db, 'schools', schoolId, 'schoolDatePeriods')),
      getDocFromServer(doc(db, 'schools', schoolId)),
    ]);
    if (!school.exists()) throw new SchoolRulePeriodError('The school is unavailable. Retry after its configuration is available.');
    return resolveSchoolHistoryYear(periods.docs.map(row => ({ ...row.data(), id: row.id })),
      new Date(), schoolRuleTimeZone(school.data()));
  } catch (error) {
    if (isSchoolRulePeriodError(error)) throw error;
    throw new SchoolRulePeriodError('Could not read Year dates from Time Periods. Retry after the school configuration is available.');
  }
}
